import { ApolloError } from 'apollo-client'
import * as React from 'react'
import { Mutation, MutationFunction, MutationResult } from 'react-apollo'

import { Alert, Button, NativeLink, Text, TextField } from 'main/ui'
import { PrivacyDisclaimer } from '../../shared/PrivacyDisclaimer'
import ForgotPassword from './ForgotPassword'
import ForgotPasswordSuccess from './ForgotPasswordSuccess'
import {
  CREATE_CREDENTIALS_MUTATION,
  CreateCredentials,
  CreateCredentials_credentials,
  CreateCredentialsVariables,
} from './gql'
import { Container, FormContainer, FormRow, TextContainer, TextRow } from './LoginForm.styles'

interface IProps {
  cta: string
  onComplete: (credentials: CreateCredentials_credentials) => void
  onForgotPassword?: () => void
  onRequestSignUp: () => void
}

interface IValidation {
  email?: string
  password?: string
}

interface IState {
  email: string
  forgotPassword: boolean
  password: string
  validationHints: IValidation
  error: boolean
}

export default class LoginForm extends React.Component<IProps, IState> {
  public static defaultProps = {
    cta: 'Log in',
  }

  public state: IState = {
    email: '',
    error: false,
    forgotPassword: false,
    password: '',
    validationHints: {},
  }

  public render() {
    return (
      <Mutation<CreateCredentials, CreateCredentialsVariables>
        mutation={CREATE_CREDENTIALS_MUTATION}
        onCompleted={this.handleCompleted}
        onError={this.handleError}
      >
        {(mutate, result) =>
          this.state.forgotPassword
            ? this.renderForgotPasswordSuccess()
            : this.renderForm(mutate, result)
        }
      </Mutation>
    )
  }

  public renderForm(
    mutate: MutationFunction<CreateCredentials, CreateCredentialsVariables>,
    { loading }: MutationResult<CreateCredentials>,
  ) {
    const { cta, onRequestSignUp } = this.props
    const { email, password, validationHints, error } = this.state

    return (
      <Container>
        {error && this.renderError()}
        <TextContainer>
          <Text>First time here? </Text>
          <NativeLink onClick={() => onRequestSignUp()}>Sign up</NativeLink>
        </TextContainer>
        <FormContainer
          onSubmit={event => {
            event.preventDefault()
            this.handleSubmit(mutate)
          }}
        >
          <FormRow>
            <TextField
              label="Email"
              placeholder="Enter your email"
              value={email}
              onChange={this.handleChangeEmail}
              validationHint={validationHints.email}
              required
              roundedCorners
            />
          </FormRow>
          <FormRow>
            <TextField
              type="password"
              label="Password"
              placeholder="Enter your password"
              value={password}
              onChange={this.handleChangePassword}
              validationHint={validationHints.password}
              required
              roundedCorners
            />
          </FormRow>
          <TextRow>
            <ForgotPassword
              email={email}
              onChangeValidationHint={emailHint =>
                this.setState({ error: false, validationHints: { email: emailHint } })
              }
              onComplete={() => {
                this.setState({ forgotPassword: true })

                if (this.props.onForgotPassword) {
                  this.props.onForgotPassword()
                }
              }}
              onError={() => this.setState({ error: true })}
            />
          </TextRow>
          <FormRow>
            <Button
              kind="rounded"
              onClick={() => this.handleSubmit(mutate)}
              disabled={loading}
              full
            >
              {loading ? 'Logging in...' : cta}
            </Button>
          </FormRow>
        </FormContainer>
        <span>
          <PrivacyDisclaimer />
        </span>
      </Container>
    )
  }

  private renderForgotPasswordSuccess() {
    return <ForgotPasswordSuccess email={this.state.email} />
  }

  private renderError() {
    return (
      <TextContainer>
        <Alert kind="danger" onDismiss={() => this.setState({ error: false })}>
          Something went wrong!
        </Alert>
      </TextContainer>
    )
  }

  private handleSubmit = (
    mutate: MutationFunction<CreateCredentials, CreateCredentialsVariables>,
  ) => {
    const { email, password } = this.state

    if (this.validate()) {
      mutate({
        variables: {
          email: email.trim(),
          password,
        },
      })
    }
  }

  private handleCompleted = (data: CreateCredentials) => {
    const { onComplete } = this.props
    const { credentials } = data
    if (!credentials) throw new Error('no credentials')

    onComplete(credentials)
  }

  private handleError = (error: ApolloError) => {
    const firstError = error.graphQLErrors[0]

    switch (firstError && firstError.message) {
      case 'email_not_found':
        return this.setState({
          validationHints: { email: 'Your email was not found', password: '' },
        })
      case 'invalid_password':
        return this.setState({
          validationHints: { email: '', password: 'Your password is incorrect' },
        })
      default:
        return this.setState({ error: true })
    }
  }

  private handleChangeEmail = (email: string) => {
    this.setState({ email })
  }

  private handleChangePassword = (password: string) => {
    this.setState({ password })
  }

  private validate() {
    const { email, password } = this.state

    const validationHints = {
      email: email ? '' : 'Please enter an email',
      password: password ? '' : 'Please enter a password',
    }

    this.setState({ error: false, validationHints })

    return !validationHints.email && !validationHints.password
  }
}
